import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { DataTable, Column } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import { ActionsPopover } from '../../../common/ActionsPopover';
import { FiltersButton, SearchTextField } from '../../Paylinks/components';
import { RefreshButton } from '../../Paylinks/components/RefreshButton';
import { Button } from '../../../components/core';
import { ActionDialogModal } from '../../Paylinks/components/ActionDialog/modal';
import { sanitizeNumericString } from '../../Paylinks/SendPaylinkForm/formValues';
import {
  useCreatePortalUserMutation,
  useDeletePortalUserMutation,
  useLazyGetPortalUserQuery,
  useResendPortalUserAccessEmailMutation,
  useSearchPortalUsersQuery,
  useUpdatePortalUserMutation,
} from '../../../store/modules/merchant/api';
import {
  CreatePortalUserBody,
  PortalUserListItem,
  PortalUserSortBy,
  PortalUserSortQuery,
  SearchPortalUsersQuery,
  UpdatePortalUserBody,
} from '../../../store/modules/merchant/types';
import { toast } from '../../../util/toast';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useIsSkipifyAdmin } from '../../../hooks';
import { shouldBlockPortalUserSelfManagement } from '../portalUserSelfManagement';
import {
  getResendPortalUserEmailActionLabel,
  getResendPortalUserEmailSuccessMessage,
} from '../portalUserEmailActions';
import { getSharedPortalUserColumns } from '../portalUserTableColumns';
import { PortalUserFormDialog } from './PortalUserFormDialog';
import { PortalUserFiltersPanel, PortalUserFilters } from './PortalUserFiltersPanel';
import { useAssignablePortalUserRoles } from '../portalUserRoleCatalog';

const filterKeys: Array<keyof PortalUserFilters> = ['roleNames', 'merchantIds'];
/** Omitted from queryParams when unset; URL sync must still remove stale values. */
const optionalUrlQueryKeys = ['searchQuery', 'sortBy', 'orderBy'] as const;
const portalUserSortFields: PortalUserSortBy[] = ['givenName', 'familyName', 'email'];

const parseSortQuery = (params: URLSearchParams): PortalUserSortQuery => {
  const sortBy = params.get('sortBy');
  const orderBy = params.get('orderBy');
  const validSortBy = portalUserSortFields.includes(sortBy as PortalUserSortBy)
    ? (sortBy as PortalUserSortBy)
    : undefined;

  return {
    ...(validSortBy ? { sortBy: validSortBy } : {}),
    ...(orderBy === 'asc' || orderBy === 'desc' ? { orderBy } : {}),
  };
};

export const PortalUsersTable = () => {
  const { merchantUserId } = useMerchant();
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('searchQuery') ?? '');
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [pageNumber, setPageNumber] = useState(
    Number(sanitizeNumericString(searchParams.get('pageNumber'))) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(sanitizeNumericString(searchParams.get('pageSize'))) || 25,
  );
  const [filters, setFilters] = useState<PortalUserFilters>(() => ({
    roleNames: searchParams.getAll('roleNames').length
      ? searchParams.getAll('roleNames')
      : undefined,
    merchantIds: searchParams.getAll('merchantIds').length
      ? searchParams.getAll('merchantIds')
      : undefined,
  }));
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string>();
  const [sortQuery, setSortQuery] = useState<PortalUserSortQuery>(() =>
    parseSortQuery(searchParams),
  );
  const tableRef = useRef<HTMLDivElement>(null);

  const queryParams = useMemo<SearchPortalUsersQuery>(
    () => ({
      pageNumber,
      pageSize,
      ...(searchQuery.trim() ? { searchQuery: searchQuery.trim() } : {}),
      ...filters,
      ...sortQuery,
    }),
    [filters, pageNumber, pageSize, searchQuery, sortQuery],
  );

  const roles = useAssignablePortalUserRoles();
  const { data: response, isFetching, refetch } = useSearchPortalUsersQuery(queryParams);
  const [getPortalUser, { data: selectedUserDetail }] = useLazyGetPortalUserQuery();
  const [createPortalUser] = useCreatePortalUserMutation();
  const [updatePortalUser] = useUpdatePortalUserMutation();
  const [deletePortalUser, { isLoading: isDeleting }] = useDeletePortalUserMutation();
  const [resendAccessEmail] = useResendPortalUserAccessEmailMutation();

  const filtersCount = useMemo(() => {
    let count = 0;
    if (filters.roleNames?.length) count += 1;
    if (filters.merchantIds?.length) count += 1;
    return count;
  }, [filters]);

  useEffect(() => {
    filterKeys.forEach((filterKey) => {
      searchParams.delete(filterKey);
    });

    optionalUrlQueryKeys.forEach((key) => {
      if (!(key in queryParams)) {
        searchParams.delete(key);
      }
    });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        searchParams.delete(key);
        return;
      }
      if (Array.isArray(value)) {
        searchParams.delete(key);
        value.forEach((item) => searchParams.append(key, item));
        return;
      }
      searchParams.set(key, String(value));
    });

    setSearchParams(searchParams, { replace: true });
  }, [queryParams, searchParams, setSearchParams]);

  const openEditDialog = async (userId: string) => {
    setSelectedUserId(userId);
    try {
      await getPortalUser(userId).unwrap();
      setFormMode('edit');
    } catch {
      toast.error('Unable to load user details.');
    }
  };

  const handleResendEmail = async (user: PortalUserListItem) => {
    try {
      await resendAccessEmail(user.userId).unwrap();
      toast.success(getResendPortalUserEmailSuccessMessage(user.hasLoggedIn));
    } catch {
      toast.error('Unable to send email. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    try {
      await deletePortalUser(deleteUserId).unwrap();
      toast.success('User deleted.');
      if (selectedUserId === deleteUserId) {
        setSelectedUserId(undefined);
        setFormMode(null);
      }
      setDeleteUserId(undefined);
    } catch {
      toast.error('Unable to delete user. Please try again.');
    }
  };

  const columns: Column<PortalUserListItem>[] = [
    ...getSharedPortalUserColumns(),
    {
      id: 'actions',
      headerName: 'Actions',
      align: 'center',
      headerAlign: 'center',
      renderCell: (row) => {
        const blockSelf = shouldBlockPortalUserSelfManagement(
          row.userId,
          merchantUserId,
          isSkipifyAdmin,
        );
        return (
          <ActionsPopover
            ariaLabel="Portal user actions"
            actions={[
              {
                label: 'Edit',
                onClick: () => openEditDialog(row.userId),
                disabled: blockSelf,
              },
              {
                label: getResendPortalUserEmailActionLabel(row.hasLoggedIn),
                onClick: () => handleResendEmail(row),
                disabled: blockSelf,
              },
              {
                label: 'Delete',
                onClick: () => setDeleteUserId(row.userId),
                disabled: blockSelf || (!!merchantUserId && row.userId === merchantUserId),
              },
            ]}
          />
        );
      },
      minWidth: 80,
      maxWidth: 80,
    },
  ];

  return (
    <>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <Stack direction="row" gap={2} alignItems="center">
            <RefreshButton
              onClick={() => {
                setSearchResetKey((key) => key + 1);
                refetch();
              }}
              disabled={isFetching}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
            <FiltersButton
              filtersCount={filtersCount}
              onClick={() => setIsFiltersPanelOpen(true)}
              disabled={isFetching}
            />
            <Button text="Add user" onClick={() => setFormMode('add')} />
          </Stack>
        </Box>
        <SearchTextField
          label="Search by email or name"
          defaultValue={searchQuery}
          resetKey={searchResetKey}
          disabled={isFetching}
          minSearchLength={3}
          onSearch={(value) => {
            if (isFetching) return;
            setPageNumber(1);
            setSearchQuery(value);
          }}
        />
        <DataTable<PortalUserListItem>
          rows={response?.data ?? []}
          columns={columns}
          getRowId={(row) => row.userId}
          isLoading={isFetching}
          loadingVariant="spinner"
          loadingMessage="Loading users…"
          pageSize={pageSize}
          sort={sortQuery}
          sortDisabled={isFetching}
          setSort={(query) => {
            if (isFetching) return;
            setPageNumber(1);
            setSortQuery(query as PortalUserSortQuery);
          }}
          tableHeight="calc(100vh - 360px)"
          noRowsMessage="No portal users found. Try changing your search or filters."
          tableRef={tableRef}
          rowSx={(row) =>
            row.userId === selectedUserId ? { backgroundColor: 'action.selected' } : {}
          }
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, page) => {
                setPageNumber(page);
                tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              page={pageNumber}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50]}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>

      <PortalUserFiltersPanel
        isOpen={isFiltersPanelOpen}
        onClose={() => setIsFiltersPanelOpen(false)}
        roles={roles}
        initialFilterState={{
          roleNames: filters.roleNames ?? [],
          merchants: [],
        }}
        onFiltersApply={(newFilters) => {
          if (isFetching) return;
          setPageNumber(1);
          setFilters(newFilters);
        }}
      />

      {formMode && (formMode === 'add' || selectedUserDetail?.userId === selectedUserId) && (
        <PortalUserFormDialog
          isOpen
          mode={formMode}
          roles={roles}
          user={formMode === 'edit' ? selectedUserDetail : undefined}
          onClose={() => {
            setFormMode(null);
            if (formMode === 'add') {
              setSelectedUserId(undefined);
            }
          }}
          onSubmit={async (body) => {
            try {
              if (formMode === 'add') {
                const created = await createPortalUser(body as CreatePortalUserBody).unwrap();
                toast.success('Portal user created.');
                setSelectedUserId(created.userId);
              } else if (selectedUserId) {
                await updatePortalUser({
                  userId: selectedUserId,
                  body: body as UpdatePortalUserBody,
                }).unwrap();
                toast.success('Portal user updated.');
              }
            } catch (error) {
              const message =
                error && typeof error === 'object' && 'data' in error
                  ? String((error as { data?: { message?: string } }).data?.message ?? '')
                  : '';
              throw new Error(message || 'Unable to save portal user.');
            }
          }}
        />
      )}

      <ActionDialogModal
        isOpen={!!deleteUserId}
        onProceed={handleDelete}
        isSubmitting={isDeleting}
        onClose={() => setDeleteUserId(undefined)}
        content={{
          heading: 'Delete this portal user?',
          helpingText:
            'This permanently deletes the portal user and removes their access to all merchants.',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Delete user',
        }}
      />
    </>
  );
};
